import { useState } from 'react'
import { EMAIL_SPAM_NOTE, SUPPORT_EMAIL } from '../config/support'
import { reportAPI } from '../services/api'
import Modal from './Modal'
import './ReportContentModal.css'

const REPORT_REASONS = [
  { value: 'copyright', label: 'Copyright / DMCA concern' },
  { value: 'prohibited', label: 'Prohibited content (manual scan, traced schematic, etc.)' },
  { value: 'other', label: 'Other (inappropriate, spam, or other issue)' },
]

function ReportContentModal({ open, onClose, type = 'repository', context = {} }) {
  const [reason, setReason] = useState('')
  const [details, setDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  if (!open) return null

  const handleSubmit = async () => {
    if (!reason) return
    setError('')
    setSubmitting(true)
    try {
      await reportAPI.submit({
        report_type: type,
        repository_slug: context.repositorySlug || null,
        repository_title: context.repositoryTitle || null,
        file_path: type === 'file' ? (context.filePath || null) : null,
        file_id: type === 'file' && context.fileId ? String(context.fileId) : null,
        commit_id: type === 'commit' && context.commitId ? String(context.commitId) : null,
        commit_hash: type === 'commit' ? (context.commitHash || null) : null,
        commit_message: type === 'commit' ? (context.commitMessage || null) : null,
        reason,
        details: details.trim() || null,
      })
      setSuccess(true)
      setReason('')
      setDetails('')
      setTimeout(() => {
        setSuccess(false)
        onClose()
      }, 2500)
    } catch (err) {
      setError(err.message || 'Failed to submit report')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setReason('')
    setDetails('')
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} maxWidth="28rem" ariaLabelledBy="report-modal-title">
      <div className="report-modal-inner">
        <div className="report-modal-header">
          <h2 id="report-modal-title">Report content</h2>
          <button type="button" className="report-modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>
        <p className="report-modal-intro">
          Your report will be added to the site moderation queue for review. Reports can also be sent to {SUPPORT_EMAIL} for urgent matters. {EMAIL_SPAM_NOTE}
        </p>
        {success && (
          <p className="report-modal-success" style={{ color: 'var(--success, #28a745)', marginBottom: '1rem', fontWeight: 500 }}>
            Report submitted. It will appear in the moderation queue.
          </p>
        )}
        {error && (
          <p className="report-modal-error" style={{ color: 'var(--error, #dc3545)', marginBottom: '1rem' }}>
            {error}
          </p>
        )}
        <div className="report-modal-context">
          <strong>Reporting:</strong>{' '}
          {type === 'repository' && (context.repositoryTitle || context.repositorySlug)}
          {type === 'file' && (context.filePath ? `File "${context.filePath}" in ${context.repositoryTitle || context.repositorySlug}` : (context.repositoryTitle || context.repositorySlug))}
          {type === 'commit' && (context.commitHash ? `Commit ${context.commitHash} in ${context.repositoryTitle || context.repositorySlug}` : (context.repositoryTitle || context.repositorySlug))}
        </div>
        <div className="report-modal-form">
          <label htmlFor="report-reason">Reason</label>
          <select
            id="report-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="report-select"
          >
            <option value="">Select a reason...</option>
            {REPORT_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <label htmlFor="report-details">Additional details (optional)</label>
          <textarea
            id="report-details"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            className="report-textarea"
            placeholder="Any extra context that would help us review..."
            rows={3}
          />
        </div>
        <div className="report-modal-actions">
          <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!reason || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit report'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default ReportContentModal
